import req from '../../api/request'

/**
 * The dropdown option reads behind the add/edit Game modal.
 *
 * Same endpoints the legacy pickers called directly on every mount
 * (`VenueFacilityPicker`, `GameTeamPicker`); pulled into services so they can
 * be cached by React Query instead of refetched each time the modal opens.
 */

export interface VenueOption {
  id: string
  name_full?: string | null
  name?: string | null
  timezone?: string | null
}

export interface FacilityOption {
  id: string
  name?: string | null
}

export interface TeamOption {
  id: string
  name_full?: string | null
  name?: string | null
  /**
   * A placeholder stands in for a team not yet determined ("Winner of
   * Semifinal 1"). It belongs to no division, which is why selecting one makes
   * the division field appear and become required.
   */
  is_placeholder?: boolean | null
}

/**
 * The venues URL, built exactly as the legacy `VenueFacilityPicker` effect did.
 *
 * Split out from the request so it can be unit tested: which venues an admin
 * can pick hinges entirely on this query string, and getting it wrong changes
 * the list silently, with no error to notice.
 *
 * `recursive` only means anything alongside a schedule scope -- it narrows a
 * competition's list to its own venues. Unscoped, the request is a bare
 * `/venues`; appending the param there would produce a malformed
 * `/venues&recursive=0`.
 */
export const buildVenuesUrl = (
  scheduleId?: string | null,
  recursive = true
): string => {
  if (typeof scheduleId !== 'string' || !scheduleId) {
    return '/venues'
  }
  const params = new URLSearchParams({ schedule_id: scheduleId })
  if (!recursive) {
    params.set('recursive', '0')
  }
  return `/venues?${params.toString()}`
}

/** GET /venues, optionally scoped to a competition. */
export const getVenues = async (
  scheduleId?: string | null,
  recursive = true
): Promise<VenueOption[]> => {
  const { data } = await req(buildVenuesUrl(scheduleId, recursive))
  return (data ?? []) as VenueOption[]
}

/** GET /venues/:venueId/facilities */
export const getVenueFacilities = async (
  venueId: string
): Promise<FacilityOption[]> => {
  const { data } = await req(`/venues/${venueId}/facilities`)
  return (data ?? []) as FacilityOption[]
}

/** GET /schedules/:scheduleId/teams/simple */
export const getScheduleTeams = async (
  scheduleId: string
): Promise<TeamOption[]> => {
  const { data } = await req(`/schedules/${scheduleId}/teams/simple`)
  return (data ?? []) as TeamOption[]
}

export interface SubCompetitionOption {
  id: string
  name_full?: string | null
  name?: string | null
}

export interface SubCompetitionLevel {
  /** What this level is called — "Division", "Pool", … — used as the label. */
  name: string
  schedules: SubCompetitionOption[]
}

/**
 * GET /schedules/:scheduleId/children/dropdown
 *
 * The competition's child levels. Only needed when a placeholder team is
 * selected: a placeholder belongs to no division, so the admin has to say which
 * one the game lands in.
 *
 * Returns the LAST level, matching the legacy `GameScheduleSelect` — the
 * deepest level is the one games are actually assigned to. Levels carrying no
 * schedules are skipped: the API sometimes reports a sub-competition type with
 * an empty list, and picking that one would leave the select permanently empty.
 */
export const getSubCompetitions = async (
  scheduleId: string
): Promise<SubCompetitionLevel> => {
  const { data } = await req(`/schedules/${scheduleId}/children/dropdown`)
  const levels = (data ?? []) as SubCompetitionLevel[]

  for (let i = levels.length - 1; i >= 0; i -= 1) {
    const level = levels[i]
    if (level?.schedules?.length) {
      return { name: level.name, schedules: level.schedules }
    }
  }

  return { name: '', schedules: [] }
}
